import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DeleteOutline, Edit } from '@mui/icons-material';
import FullScreenImageViewer from '../../components/FullScreenImageViewer';
import KomootPreviewCard from '../../components/KomootPreviewCard';
import LinkifiedText from '../../components/LinkifiedText';
import { SUPPORTED_IMAGE_ACCEPT, SUPPORTED_IMAGE_FORMAT_LABEL } from '../../constants/imageUpload';
import { formatDate } from '../../utils/format';
import {
  isGuideType,
  isLocationType,
  isProblemType,
  supportsContentCategories,
} from './config';
import { getSeverityChip, getStatusChip, Place, renderLocationLink } from './helpers';
import {
  HouseContent,
  HouseContentFormData,
  HouseContentSeverity,
  HouseContentStatus,
  HouseContentType,
  PageText,
  TranslateFunction,
} from './types';

interface ContentViewDialogProps {
  open: boolean;
  contentType: HouseContentType;
  selectedItem: HouseContent | null;
  imageViewerOpen: boolean;
  selectedImageIndex: number;
  canEditSelectedItem: boolean;
  language: string;
  t: TranslateFunction;
  onClose: () => void;
  onStartEdit: () => void;
  onOpenImageViewer: (index: number) => void;
  onCloseImageViewer: () => void;
  onNavigateImage: (index: number) => void;
}

export const ContentViewDialog: React.FC<ContentViewDialogProps> = ({
  open,
  contentType,
  selectedItem,
  imageViewerOpen,
  selectedImageIndex,
  canEditSelectedItem,
  language,
  t,
  onClose,
  onStartEdit,
  onOpenImageViewer,
  onCloseImageViewer,
  onNavigateImage,
}) => (
  <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{selectedItem?.title}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {selectedItem?.images?.[0]?.url && (
            <Box
              component="img"
              src={selectedItem.images[0].url}
              alt={selectedItem.images[0].originalName || selectedItem.title}
              onClick={() => onOpenImageViewer(0)}
              onKeyDown={(event: React.KeyboardEvent<HTMLImageElement>) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onOpenImageViewer(0);
                }
              }}
              role="button"
              tabIndex={0}
              sx={{ width: '100%', height: { xs: 240, md: 320 }, objectFit: 'cover', borderRadius: 4, mb: 3, cursor: 'zoom-in' }}
            />
          )}

          {supportsContentCategories(contentType) && selectedItem?.category && (
            <Typography variant="caption" color="primary" sx={{ mb: 2, display: 'block' }}>
              {selectedItem.category}
            </Typography>
          )}

          {contentType === 'activities' && selectedItem?.status && (
            <Box sx={{ mb: 2 }}>{getStatusChip(selectedItem.status, t)}</Box>
          )}

          {isProblemType(contentType) && selectedItem && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {getSeverityChip(selectedItem.severity, t)}
              {getStatusChip(selectedItem.status, t)}
            </Box>
          )}

          <LinkifiedText text={selectedItem?.description} variant="body1" sx={{ mb: 2 }} />

          {(selectedItem?.location || selectedItem?.address || (selectedItem?.latitude != null && selectedItem?.longitude != null)) && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Place fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {renderLocationLink(selectedItem.location, selectedItem.latitude, selectedItem.longitude, false, selectedItem.address)}
              </Typography>
            </Box>
          )}

          {contentType === 'activities' && selectedItem?.komootUrl && (
            <KomootPreviewCard url={selectedItem.komootUrl} sx={{ mt: 2 }} />
          )}

          {isProblemType(contentType) && selectedItem && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              {t('common.labels.reportedOn', { date: formatDate(selectedItem.createdAt, language) })}
            </Typography>
          )}

          {selectedItem?.images && selectedItem.images.length > 0 && (
            <Box sx={{ mt: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {selectedItem.images.map((image, index) => (
                <Box
                  key={image.url}
                  component="img"
                  src={image.url}
                  alt={image.originalName || selectedItem.title}
                  onClick={() => onOpenImageViewer(index)}
                  onKeyDown={(event: React.KeyboardEvent<HTMLImageElement>) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onOpenImageViewer(index);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  sx={{ width: { xs: '100%', sm: 180 }, height: 140, objectFit: 'cover', borderRadius: 3, cursor: 'zoom-in' }}
                />
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {selectedItem && canEditSelectedItem && (
          <Button onClick={onStartEdit} startIcon={<Edit />}>
            {t('common.actions.edit')}
          </Button>
        )}
        <Button onClick={onClose}>{t('common.actions.close')}</Button>
      </DialogActions>
    </Dialog>

    <FullScreenImageViewer
      images={selectedItem?.images ?? []}
      open={imageViewerOpen}
      selectedIndex={selectedImageIndex}
      title={selectedItem?.title}
      onClose={onCloseImageViewer}
      onNavigate={onNavigateImage}
    />
  </>
);

interface ContentEditDialogProps {
  open: boolean;
  contentType: HouseContentType;
  selectedItem: HouseContent | null;
  formData: HouseContentFormData;
  pageText: PageText;
  selectedImages: File[];
  existingImages: HouseContent['images'];
  categoryOptions: string[];
  isOwner: boolean;
  formErrors?: { latitude?: string; longitude?: string };
  t: TranslateFunction;
  onClose: () => void;
  onSubmit: () => void;
  onFormChange: (nextValue: HouseContentFormData) => void;
  onImagesChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveSelectedImage: (imageName: string) => void;
  onRemoveExistingImage: (imageKey: string) => void;
}

export const ContentEditDialog: React.FC<ContentEditDialogProps> = ({
  open,
  contentType,
  selectedItem,
  formData,
  pageText,
  selectedImages,
  existingImages,
  categoryOptions,
  isOwner,
  formErrors,
  t,
  onClose,
  onSubmit,
  onFormChange,
  onImagesChange,
  onRemoveSelectedImage,
  onRemoveExistingImage,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth={isGuideType(contentType) ? 'md' : 'sm'} fullWidth>
    <DialogTitle>{selectedItem ? pageText.editTitle : pageText.createTitle}</DialogTitle>
    <DialogContent>
      <Box sx={{ pt: 1 }}>
        <TextField
          fullWidth
          label={t('common.fields.title')}
          value={formData.title}
          onChange={(event) => onFormChange({ ...formData, title: event.target.value })}
          sx={{ mb: 2 }}
          required
        />

        {supportsContentCategories(contentType) && (
          <Autocomplete
            freeSolo
            options={categoryOptions}
            value={formData.category}
            inputValue={formData.category}
            onChange={(_event, value) => onFormChange({
              ...formData,
              category: typeof value === 'string' ? value : '',
            })}
            onInputChange={(_event, value) => onFormChange({ ...formData, category: value })}
            clearOnBlur={false}
            selectOnFocus
            handleHomeEndKeys
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label={pageText.formCategoryLabel || t('common.fields.category')}
                helperText={pageText.formCategoryHelper}
              />
            )}
            sx={{ mb: 2 }}
          />
        )}

        <TextField
          fullWidth
          label={pageText.formDescriptionLabel}
          value={formData.description}
          onChange={(event) => onFormChange({ ...formData, description: event.target.value })}
          multiline
          rows={isGuideType(contentType) ? 10 : 4}
          sx={{ mb: 2 }}
        />

        {isLocationType(contentType) && (
          <>
            <TextField
              fullWidth
              label={t('common.fields.address')}
              value={formData.address}
              onChange={(event) => onFormChange({ ...formData, address: event.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label={t('common.fields.location')}
              value={formData.location}
              onChange={(event) => onFormChange({ ...formData, location: event.target.value })}
              sx={{ mb: 2 }}
            />
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('common.labels.latitude')}
                  value={formData.latitude}
                  onChange={(event) => onFormChange({ ...formData, latitude: event.target.value })}
                  type="number"
                  inputProps={{ step: 'any' }}
                  error={!!formErrors?.latitude}
                  helperText={formErrors?.latitude || t('common.helpers.optionalCoordinates')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('common.labels.longitude')}
                  value={formData.longitude}
                  onChange={(event) => onFormChange({ ...formData, longitude: event.target.value })}
                  type="number"
                  inputProps={{ step: 'any' }}
                  error={!!formErrors?.longitude}
                  helperText={formErrors?.longitude}
                />
              </Grid>
            </Grid>
            {contentType === 'activities' && (
              <TextField
                fullWidth
                label={t('common.labels.komootUrl')}
                value={formData.komootUrl}
                onChange={(event) => onFormChange({ ...formData, komootUrl: event.target.value })}
                helperText={t('common.helpers.komootOptional')}
              />
            )}
          </>
        )}

        {isProblemType(contentType) && (
          <>
            <FormControl fullWidth sx={{ mb: isOwner ? 2 : 0 }}>
              <InputLabel>{t('common.fields.severity')}</InputLabel>
              <Select
                value={formData.severity}
                label={t('common.fields.severity')}
                onChange={(event) => onFormChange({ ...formData, severity: event.target.value as HouseContentSeverity })}
              >
                <MenuItem value="low">{t('common.statuses.low')}</MenuItem>
                <MenuItem value="medium">{t('common.statuses.medium')}</MenuItem>
                <MenuItem value="high">{t('common.statuses.high')}</MenuItem>
              </Select>
            </FormControl>
            {isOwner && (
              <FormControl fullWidth>
                <InputLabel>{t('common.fields.status')}</InputLabel>
                <Select
                  value={formData.status}
                  label={t('common.fields.status')}
                  onChange={(event) => onFormChange({ ...formData, status: event.target.value as HouseContentStatus })}
                >
                  <MenuItem value="reported">{t('common.statuses.reported')}</MenuItem>
                  <MenuItem value="in_progress">{t('common.statuses.inProgress')}</MenuItem>
                  <MenuItem value="resolved">{t('common.statuses.resolved')}</MenuItem>
                </Select>
              </FormControl>
            )}
          </>
        )}

        <Button component="label" variant="outlined" sx={{ mt: 2 }}>
          {selectedItem ? t('houseDialog.addMorePhotos') : t('houseDialog.addPhotos')}
          <input hidden type="file" accept={SUPPORTED_IMAGE_ACCEPT} multiple onChange={onImagesChange} />
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {t('common.labels.supportedFormats', { formats: SUPPORTED_IMAGE_FORMAT_LABEL })}
        </Typography>

        {existingImages && existingImages.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('common.labels.currentPhotos')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {existingImages.map((image) => (
                <Box key={image.key} sx={{ position: 'relative' }}>
                  <Box
                    component="img"
                    src={image.url}
                    alt={image.originalName || formData.title}
                    sx={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 1 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => onRemoveExistingImage(image.key)}
                    sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'background.paper' }}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {selectedImages.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {selectedImages.map((image) => (
              <Alert
                key={`${image.name}-${image.size}`}
                severity="info"
                action={(
                  <Button color="inherit" size="small" onClick={() => onRemoveSelectedImage(image.name)}>
                    {t('common.actions.remove')}
                  </Button>
                )}
                sx={{ m: 0 }}
              >
                {pageText.readyToUpload ? `${image.name} - ${pageText.readyToUpload}` : image.name}
              </Alert>
            ))}
          </Box>
        )}
      </Box>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
      <Button onClick={onSubmit} variant="contained">
        {selectedItem ? t('common.actions.update') : isProblemType(contentType) ? t('common.actions.report') : t('common.actions.create')}
      </Button>
    </DialogActions>
  </Dialog>
);